/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CustomListbox from 'components/listbox'

// Api
import { getLocality, getProvince } from 'api/address'

// Utils
import { trackEvent } from 'analytics/trackFunctions'
import { nonNumericalChars } from 'utils'

// Components
import PageWrapper from 'components/PageWrapper'
import TextInputField from 'components/input'
import PrimaryButton from 'components/primaryButton'
import { requestNewCard } from 'api/credit-card'
import { Controller, useForm } from 'react-hook-form'
import PhysicalCardRequestErrorModal from 'components/PhysicalCardRequestErrorModal'

const WayniCardUpdateAddress = () => {
  const navigate = useNavigate()

  // request error modal
  const [isModalOpen, setIsModalOpen] = useState(false)

  // listboxes states
  const [region, setRegion] = useState(null)
  const [city, setCity] = useState(null)
  const [listboxError, setListboxError] = useState({
    region: false,
    city: false
  })

  // input states
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      street_name: '',
      street_number: '',
      zip_code: '',
      floor: '',
      apartment: ''
    }
  })

  // lists
  const [regions, setRegions] = useState([])
  const [cities, setCities] = useState([])
  const [isUpdatingAddress, setIsUpdatingAddress] = useState(false)

  useEffect(() => {
    trackEvent({
      eventName: 'bille_pantalla_tarjeta_fisica_actualizar_direccion'
    })
    getProvince()
      .then((data) => setRegions(data))
      .catch((error) => console.warn(error))
    trackEvent({ eventName: 'bille_pantalla_tarjeta_fisica_nueva_direccion' })
  }, [])

  const onRegionChange = (region) => {
    setRegion(region)
    setCities([])
    setCity(null)
    getLocality(region.key).then((res) => {
      setCities(res)
    })
  }

  const handleCardPhysical = async ({
    street_name,
    street_number,
    zip_code,
    floor = '',
    apartment = ''
  }) => {
    if (isUpdatingAddress) return

    if (!region?.name) {
      setListboxError((prev) => ({ ...prev, region: true }))
      return
    }

    if (!city?.name) {
      setListboxError((prev) => ({ ...prev, city: true }))
      return
    }

    setIsUpdatingAddress(true)

    const params = {
      card_type: 'PHYSICAL',
      address: {
        region: region.key,
        city: city.key,
        street_name,
        street_number,
        zip_code,
        floor: floor.trim() ? floor : null,
        apartment: apartment.trim() ? apartment : null
      }
    }

    try {
      const res = await requestNewCard(params)
      if (res.success) {
        sessionStorage.setItem('address', JSON.stringify(res.data?.address))
        trackEvent({
          eventName: 'bille_boton_tarjeta_fisica_solicitar_ok',
          props: {
            provincia: region.name,
            ciudad: city.name,
            calle: street_name,
            altura: street_number,
            piso: floor,
            cp: zip_code,
            depto: apartment
          }
        })
        navigate('/seguimiento-tarjeta-fisica')
      } else {
        trackEvent({
          eventName: 'bille_boton_tarjeta_fisica_solicitar_error',
          props: {
            error: JSON.stringify(res)
          }
        })
        setIsModalOpen(true)
      }
    } catch (error) {
      trackEvent({
        eventName: 'bille_boton_tarjeta_fisica_solicitar_error_catch',
        props: {
          error: JSON.stringify(error)
        }
      })
      setIsModalOpen(true)
      console.error(error)
    } finally {
      setIsUpdatingAddress(false)
    }
  }

  useEffect(() => {
    setListboxError((prev) => ({
      ...prev,
      city: city?.name ? false : prev.city,
      region: region?.name ? false : prev.region
    }))
  }, [city, region])

  return (
    <PageWrapper
      viewSelected='/mis-tarjetas'
      className='!px-0 md:px-4'
      title='Tarjeta Wayni'
      noTabBar
    >
      <section className='px-4 text-black md:flex md:max-w-564 md:flex-col mb-20 md:mb-5 mx-auto'>
        <div className='w-full items-center text-black justify-center flex-col text-center'>
          <h1 className=' font-bold text-2xl mt-2'>
            ¡Tener Tarjeta Wayni es <br /> muy fácil!
          </h1>
          <p className=' text-xs font-medium my-3'>
            Completá los datos de la dirección en <br /> donde querés recibirla
          </p>
        </div>
        <form onSubmit={handleSubmit(handleCardPhysical)}>
          <CustomListbox
            label='Provincia'
            selectedValue={region}
            setSelectedValue={(e) => onRegionChange(e)}
            list={regions}
            isError={listboxError.region}
            errorMessage='Campo requerido'
          />

          <CustomListbox
            disabled={!cities || !region}
            className='mt-2'
            label='Ciudad'
            selectedValue={city}
            setSelectedValue={setCity}
            list={cities}
            isError={listboxError.city}
            errorMessage='Campo requerido'
          />

          <Controller
            rules={{
              required: {
                value: true,
                message: 'Campo requerido'
              },
              maxLength: 60,
              validate: (value) => value.trim() !== '' || 'Campo requerido'
            }}
            name='street_name'
            control={control}
            defaultValue=''
            render={({ field: { value, onChange, name } }) => (
              <TextInputField
                id='street_name'
                value={value}
                name={name}
                onChange={onChange}
                inputType='text'
                label='Calle'
                maxLength={60}
                isError={!!formState.errors?.street_name}
                errorMessage={formState.errors?.street_name?.message}
              />
            )}
          />

          <Controller
            rules={{
              required: {
                value: true,
                message: 'Campo requerido'
              },
              pattern: {
                value: /^(0|[1-9]\d*)(\.\d+)?$/,
                message: 'El campo debe ser numérico'
              },
              maxLength: 6,
              validate: (value) => value.trim() !== '' || 'Campo requerido'
            }}
            name='street_number'
            control={control}
            defaultValue=''
            render={({ field: { value, onChange, name } }) => (
              <TextInputField
                value={value}
                name={name}
                onChange={onChange}
                onKeyDown={(e) => {
                  if (nonNumericalChars.includes(e.key)) e.preventDefault()
                }}
                id='street_number'
                inputType='number'
                label='Altura'
                maxLength={6}
                isError={!!formState.errors?.street_number}
                errorMessage={formState.errors?.street_number?.message}
              />
            )}
          />

          <Controller
            rules={{
              maxLength: 3
            }}
            name='floor'
            control={control}
            defaultValue=''
            render={({ field: { value, onChange, name } }) => (
              <TextInputField
                value={value}
                name={name}
                onChange={onChange}
                onKeyDown={(e) => {
                  if (nonNumericalChars.includes(e.key)) e.preventDefault()
                }}
                id='floor'
                inputType='number'
                label='Piso (opcional)'
                maxLength={3}
                isError={!!formState.errors?.floor}
                errorMessage={formState.errors?.floor?.message}
              />
            )}
          />

          <Controller
            rules={{
              maxLength: 3
            }}
            name='apartment'
            control={control}
            defaultValue=''
            render={({ field: { value, onChange, name } }) => (
              <TextInputField
                id='apartment'
                value={value}
                name={name}
                onChange={onChange}
                inputType='text'
                label='Departamento (opcional)'
                maxLength={3}
                isError={!!formState.errors?.apartment}
                errorMessage={formState.errors?.apartment?.message}
              />
            )}
          />

          <Controller
            rules={{
              required: {
                value: true,
                message: 'Campo requerido'
              },
              maxLength: 4,
              minLength: {
                value: 4,
                message: 'El campo debe tener 4 dígitos'
              },
              validate: (value) => value.trim() !== '' || 'Campo requerido'
            }}
            name='zip_code'
            control={control}
            defaultValue=''
            render={({ field: { value, onChange, name } }) => (
              <TextInputField
                value={value}
                name={name}
                onChange={onChange}
                id='zip_code'
                inputType='number'
                onKeyDown={(e) => {
                  if (nonNumericalChars.includes(e.key)) e.preventDefault()
                }}
                label='Código postal'
                maxLength={4}
                isError={!!formState.errors?.zip_code}
                errorMessage={formState.errors?.zip_code?.message}
              />
            )}
          />
          <PrimaryButton
            primaryButtonFunction={() => {
              setListboxError({ region: !region?.name, city: !region?.name })
            }}
            buttonText='Solicitar'
            className='mt-4'
            type='submit'
            isLoading={isUpdatingAddress}
          />
        </form>
        <PhysicalCardRequestErrorModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
        />
      </section>
    </PageWrapper>
  )
}

export default WayniCardUpdateAddress
